import React, { useEffect } from "react";
import { connect } from "react-redux";
import Box from "@mui/material/Box";
import { MSpinner } from "components/Shared";
import MGridLayout, { MGridLayoutItem } from "components/Shared/MGridLayout";
import CustomerDetails from "components/Customer/Profile/CustomerDetails";
import CustomerDetailsQadDisplay from "components/QualityAssurance/CustomerQAEmployeesDisplay.tsx";
import FeaturePermissionStates from "components/Shared/FeaturePermissionStates.tsx";
import { CustomerDetailsDto } from "interfaces/Mueller.Business.Models.ts";
import FeaturePermissionTypeCodes from "typeConstants/featurePermissionCodes.ts";
import CustomerEmailCaseSettings from "components/Customer/Profile/CustomerEmailCaseSettings.tsx";
import CustomerNavigationCard from "components/Customer/Profile/CustomerNavigationCard.tsx";
import CustomerSalesDetails from "./CustomerSalesDetails.tsx";
import CustomerCalcSettingsGrid from "../CalcSettings/CustomerCalcSettingsGrid.tsx";
import CustomerExperienceAssignment from "../CustomerExperienceAssignment.tsx";
import PDFExportSettings from "./PDFExportSettings.tsx";
import CustomerInsightAccountNotificationList from "./CustomerInsightAccountNotificationList.tsx";
import { updateHeader } from "actions/layout/index.js";
import { withRouter } from "tools/routerCompat";
import { HeaderDefinition } from "typeConstants/header/headerDefinitions.ts";

interface CustomerProfileProps {
  verticalDisplay: boolean;
  // redux
  history: any;
  updateHeader: (headerDefinitions: HeaderDefinition) => void;
  customerDetails: CustomerDetailsDto;
  fetchingCustomerDetails: boolean;
}

const CustomerProfile = ({
  customerDetails,
  fetchingCustomerDetails,
  verticalDisplay,
  history,
  updateHeader,
}: CustomerProfileProps) => {

  useEffect(() => {
    if (verticalDisplay) return;

    updateHeader({
      buttons: [{
        name: "Back to Customer List",
        onClick: () => history.goBack(),
      }]
    });

    return () => {
      updateHeader({ buttons: []});
    };
  }, [verticalDisplay]); 

  if (fetchingCustomerDetails) return (<MSpinner />);

  if (!customerDetails) return (<div>No Customer Selected. Please select a customer in Maintenance {">"} Account List and view details in full screen</div>);

  const rows: MGridLayoutItem[][] = [
    // Row 1: Customer Details, Sales Details
    [
      { component: <CustomerDetails />, size: 6 },
      { component: <CustomerSalesDetails customerNumber={customerDetails?.number} />, size: 6 },
    ],
    // Row 2: Insight Notifications, QAD Display
    [
      { component: <CustomerInsightAccountNotificationList customerNumber={customerDetails?.number} />, size: 6 },
      { component: <CustomerDetailsQadDisplay />, size: 6 },
    ],
    // Row 3: Customer Experience, Email Case Settings
    [
      { component: <CustomerExperienceAssignment verticalDisplay={verticalDisplay} customerNumber={customerDetails?.number} />, size: 6 },
      { component: <CustomerEmailCaseSettings verticalDisplay={verticalDisplay} />, size: 6 },
    ],
    // Row 4: Feature Permissions, Navigation Card
    [
      { 
        component: (
          <FeaturePermissionStates
            customerNumber={customerDetails?.number}
            featurePermissionType={FeaturePermissionTypeCodes.CALL_CENTER_INCLUSION}
          />
        ), 
        size: 6 
      },
      { 
        component: (
          <CustomerNavigationCard 
            customerNumber={customerDetails?.number}
            verticalDisplay={verticalDisplay}
          />
        ), 
        size: 6 
      },
    ],
    // Row 5: Calc Settings (full width)
    [
      { component: <CustomerCalcSettingsGrid customerNumber={customerDetails?.number} />, size: 12 },
    ],
    // Row 6: PDF Export Settings
    [
      { component: <PDFExportSettings customerNumber={customerDetails?.number} />, size: 6 },
    ],
  ];

  return (
    <Box sx={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
      <MGridLayout
        verticalDisplay={verticalDisplay}
        rows={rows}
        padding={verticalDisplay ? 0 : 1}
      />
    </Box>
  );
};

const mapStateToProps = (state) => ({
  customerDetails: state.customer.customerDetails,
  fetchingCustomerDetails: state.customer.fetchingCustomerDetails,
});

const mapDispatchToProps = (dispatch) => ({
  updateHeader(headerDefinitions: HeaderDefinition) {
    dispatch(updateHeader(headerDefinitions));
  },
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(CustomerProfile));